import type { FC } from 'react';
import type { TopBarTypes } from './types';

import { useEffect, useMemo, useRef, useState } from 'react';

import styled, { css } from 'styled-components';

import { Wrapper, Button, Download } from '../Common';
import { HoverableDropzone } from '../HoverableDropzone';

import { useSales, useWebsiteEditor } from 'shared/hooks/index';
import {
  ApiService,
  COLOR_BLUE_3,
  baseMediaEndpoint,
  defaultWebValue,
  generateAccessKey
} from 'shared/helpers/index';
import { Image as ImageComp } from 'shared/components/Common';

const Container = styled.div<{ backgroundColor: string }>`
  background: ${(props) => props.backgroundColor};
  padding: 18px 0;
  color: #fff;
  position: relative;
  z-index: 23;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ theme: { $width } }) => $width <= 800 && 'flex-direction: column;'}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  max-height: 160px;
  height: 80px;
  max-width: 400px;
  width: 288px;
  color: black;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    margin-bottom: 30px;
  `}

  & img {
    border-radius: 0px;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center center;
  }
`;

const BtnInner = styled.div`
  flex-shrink: 0;
  ${({ theme: { $width } }) =>
    $width <= 800 &&
    `
    width: 100%;
    display: flex;
    justify-content: center;
  `}
`;

const buttonStyles = css`
  position: relative;
  z-index: 99;
  padding-top: 17px;
  padding-bottom: 17px;
  max-width: 480px;

  ${({ theme: { $width } }) =>
    $width <= 800 &&
    `
    margin-top: 8px;
    width: 70%;
    margin: 0 auto;
    padding-top: 20px;
    padding-bottom: 20px;
  `}

  @media (max-width: 575px) {
    display: block;
    margin: 0 auto;
  }
`;

const StyledButton = styled((props) => <Button {...props} />)`
  ${buttonStyles}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

export const V2: FC<TopBarTypes> = (props) => {
  const {
    button = defaultWebValue,
    hidden,
    secondaryColor = COLOR_BLUE_3,
    customBackgroundColor,
    meta = {},
    title = defaultWebValue,
    textColor,
    order = 0
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const {
    salesPageId,
    updateImageMeta,
    type,
    salesPageValues,
    deliveryPageValues,
    purchaseElementId
  } = useSales();

  const [loading, setLoading] = useState(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const { isDashboard } = useSales();

  const logoImage = useMemo(() => {
    if (meta['image']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      return meta['image'];
    }
    return '/img/components/topbar/logoDefUpl.svg';
  }, [meta]);

  const { containerClassname, editable, websiteAssembler } = useWebsiteEditor();

  // Accessibility improvements
  const buttonAccessKey = useMemo(() => generateAccessKey('tb2', order), [order]);
  const skipLinkId = useMemo(
    () => `skip-to-topbar-v2-${meta?.['accessorKey'] || 'section'}`,
    [meta]
  );
  const logoId = useMemo(() => `topbar-logo-${meta?.['accessorKey'] || 'image'}`, [meta]);
  const regionLabel = useMemo(() => {
    return isSticky ? 'Sticky navigation bar with logo' : 'Top navigation bar with logo';
  }, [isSticky]);

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Logo upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File) => {
    try {
      setLoading(true);
      if (!meta['accessorKey'] || !editable) return;
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          setResize({ width, height });
          updateImageMeta(meta['accessorKey'], width, 'width');
          updateImageMeta(meta['accessorKey'], height, 'height');
        };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, 'image');
      setLoading(false);

      // Announce successful upload
      const successAnnouncement = document.createElement('div');
      successAnnouncement.setAttribute('aria-live', 'polite');
      successAnnouncement.setAttribute('aria-atomic', 'true');
      successAnnouncement.className = 'sr-only';
      successAnnouncement.textContent = 'Logo uploaded successfully';
      document.body.appendChild(successAnnouncement);
      setTimeout(() => document.body.removeChild(successAnnouncement), 1000);
    } catch (error) {
      handleUploadError(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateSticky = () => {
      const checkValues = type === 'sales' ? salesPageValues : deliveryPageValues;
      if (!ref.current || !ref.current.parentElement || websiteAssembler || !checkValues) return;
      const values = checkValues[meta['accessorKey']];

      const minOrder = Math.min(
        ...Object.values(checkValues)
          .filter((item) => typeof item === 'object')
          .map((item) => item['order'])
          .filter((order) => typeof order === 'number')
      );

      if (typeof values === 'object' && values.order !== minOrder) return;

      const top = ref.current.parentElement.getBoundingClientRect().top;

      if (top < 0) {
        ref.current.classList.add('pw-sticky-topbar');
        setIsSticky(true);

        // Announce sticky state to screen readers
        const stickyAnnouncement = document.createElement('div');
        stickyAnnouncement.setAttribute('aria-live', 'polite');
        stickyAnnouncement.setAttribute('aria-atomic', 'true');
        stickyAnnouncement.className = 'sr-only sticky-announcement';
        stickyAnnouncement.textContent = 'Navigation bar is now sticky';

        const existsAlready = document.querySelector('.sticky-announcement');
        if (!existsAlready) {
          document.body.appendChild(stickyAnnouncement);
          setTimeout(() => {
            if (stickyAnnouncement.parentNode) {
              document.body.removeChild(stickyAnnouncement);
            }
          }, 1000);
        }
      } else if (top >= 0) {
        ref.current.classList.remove('pw-sticky-topbar');
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', updateSticky);
    window.addEventListener('resize', updateSticky);

    return () => {
      window.removeEventListener('scroll', updateSticky);
      window.removeEventListener('resize', updateSticky);
    };
  }, [deliveryPageValues, websiteAssembler, meta, salesPageValues, type]);

  const href = useMemo(() => {
    if (meta['url']) return meta['url'];
    return purchaseElementId;
  }, [meta, purchaseElementId]);

  const resolvedBackgroundColor = useMemo(() => {
    if (customBackgroundColor) {
      return customBackgroundColor;
    }
    return secondaryColor;
  }, [customBackgroundColor, secondaryColor]);

  // Determine button type for accessibility
  const buttonType = useMemo(() => {
    if (meta['download']) return 'download';
    if (meta['community']) return 'community link';
    if (meta['event']) return 'event registration';
    if (meta['url']) return 'external link';
    if (type === 'delivery') return 'delivery action';
    return 'purchase button';
  }, [meta, type]);

  // Generate alt text for logo
  const logoAltText = useMemo(() => {
    if (meta['image_link']) {
      return 'Company logo - click to visit homepage';
    }
    return 'Company logo';
  }, [meta]);

  return (
    <Container
      ref={ref}
      id="sales_topbar_component"
      backgroundColor={resolvedBackgroundColor}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      role="banner"
      aria-label={regionLabel}
      aria-hidden={hidden && !websiteAssembler}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to top bar with logo</a>
      </span>

      <Wrapper id={skipLinkId}>
        <ImageContainer role="navigation" aria-label="Top bar navigation with logo">
          {/* Loading announcement for screen readers */}
          {loading && (
            <div className="sr-only" aria-live="assertive" aria-atomic="true">
              Uploading logo image...
            </div>
          )}

          <HoverableDropzone
            isSalePage
            css={imageStyles as never}
            disabled={!editable}
            allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
            enableLinks
            updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
            onUpload={handleUpload}
            loading={loading}
            alreadyUploaded={Boolean(logoImage)}
            imageFile={logoImage}
            resizeSelector={editable}
            initialPosition={{
              x: meta['x_1'] || 0,
              y: meta['y_1'] || 0,
              width: meta['d_width_1'],
              height: meta['d_height_1']
            }}
            meta={meta}
            positionEnabled
            handlePosition={(width: number, height: number, x: number, y: number) => {
              updateImageMeta(meta['accessorKey'], x, 'x_1');
              updateImageMeta(meta['accessorKey'], y, 'y_1');
              updateImageMeta(meta['accessorKey'], width, 'd_width_1');
              updateImageMeta(meta['accessorKey'], height, 'd_height_1');
            }}
            handleResize={({ width, height }) => {
              setResize({ width, height });
              updateImageMeta(meta['accessorKey'], width, 'width');
              updateImageMeta(meta['accessorKey'], height, 'height');
            }}
            style={{
              ...(resize.height && { height: resize.height }),
              ...(resize.width && { width: resize.width })
            }}
            size="sm"
            ariaLabel="Logo upload zone">
            <ImageComp
              id={logoId}
              width={resize.width || 500}
              height={resize.height || 500}
              src={logoImage}
              alt={logoAltText}
              loading="eager"
              {...(!isDashboard &&
                meta['image_link'] && {
                  tabIndex: 0,
                  role: 'link',
                  'aria-label': `Company logo - navigate to ${meta['image_link']}`,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      window.location.href = meta['image_link'];
                    }
                  }
                })}
            />
          </HoverableDropzone>

          <BtnInner role="group" aria-label={`Action button area - ${buttonType}`}>
            {!['form', 'recurring_form', 'upsell_vip_form'].includes(href) ||
            meta['url'] ||
            type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                isBotPosition
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': logoId
                  // 'aria-pressed': isButtonActionModalOpen ? 'true' : 'false'
                })}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                id={title.id}
                isBotPosition
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': logoId
                  // 'aria-pressed': isButtonActionModalOpen ? 'true' : 'false'
                })}
              />
            )}
          </BtnInner>
        </ImageContainer>
      </Wrapper>

      {/* Screen reader announcement for sticky state */}
      {isSticky && (
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          Navigation bar with logo is currently sticky and will remain visible while scrolling
        </div>
      )}
    </Container>
  );
};

V2.displayName = 'Topbar V2';
